import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SparklesIcon, CheckCircleIcon, CalendarDaysIcon, XMarkIcon, ClipboardDocumentCheckIcon, PhotoIcon, ArrowTopRightOnSquareIcon, MapPinIcon, ChatBubbleLeftRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import logo from '../assets/theProposal-logo.png';
import homepageService, { HomepageDressEntry } from '../services/homepage';
import { locationService, Location } from '../services/location';
import { HomepageBanner, Designer } from '../types';
import BannerSlider from '../components/BannerSlider';

const Landing: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [banners, setBanners] = useState<HomepageBanner[]>([]);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [showDesignersModal, setShowDesignersModal] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [styleMatcherDresses, setStyleMatcherDresses] = useState<HomepageDressEntry[]>([]);
  const [collectionDresses, setCollectionDresses] = useState<HomepageDressEntry[]>([]);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterError, setNewsletterError] = useState('');

  useEffect(() => {
    // If user is authenticated, redirect to home
    if (!loading && user) {
      navigate('/home');
    }
  }, [user, loading, navigate]);

  // Fetch homepage banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const fetchedBanners = await homepageService.getBanners();
        // Filter to only include banners with images
        const imageBanners = fetchedBanners.filter(
          (banner: HomepageBanner) => banner.s3Url || banner.imagePath
        );
        setBanners(imageBanners);
      } catch (error) {
        console.error('Failed to load banners:', error);
      }
    };
    fetchBanners();
  }, []);

  // Fetch featured designers
  useEffect(() => {
    const fetchDesigners = async () => {
      try {
        const data = await homepageService.getFeaturedDesigners();
        setDesigners(data);
      } catch (error) {
        console.error('Failed to load designers:', error);
      }
    };
    fetchDesigners();
  }, []);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await locationService.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to load locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Fetch homepage dresses
  useEffect(() => {
    const fetchHomepageDresses = async () => {
      try {
        const [smDresses, colDresses] = await Promise.all([
          homepageService.getHomepageDresses('style_matcher'),
          homepageService.getHomepageDresses('collection'),
        ]);
        setStyleMatcherDresses(smDresses);
        setCollectionDresses(colDresses);
      } catch (error) {
        console.error('Failed to load homepage dresses:', error);
      } finally {
        setCollectionLoading(false);
      }
    };
    fetchHomepageDresses();
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterError('');
    setNewsletterLoading(true);
    try {
      const response = await apiService.post<{ message: string }>('/newsletter/subscribe', {
        email: newsletterEmail,
        name: newsletterName || undefined
      });
      setNewsletterSubmitted(true);
      setNewsletterMessage(response.data.message);
    } catch (err: any) {
      // Show validation errors verbatim, but suppress internal errors
      // (CSRF, 5xx, etc.) so we never leak backend details to the user.
      const code = err.response?.data?.code;
      if (code === 'VAL_001' && err.response?.data?.error) {
        setNewsletterError(err.response.data.error);
      } else {
        setNewsletterError("We couldn't subscribe you right now. Please try again in a moment.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300 ${scrollY > 50 ? 'py-2' : 'py-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <img src={logo} alt="The Proposal Bridal" className="h-10 transition-transform duration-300 hover:scale-105" />
            <Link 
              to="/signup" 
              className="bg-black text-white hover:bg-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Start Your Bridal Journey
            </Link>
          </div>
        </div>
      </nav>

      {/* Banner Hero Section - Only show if there are banners */}
      {banners.length > 0 && (
        <section className="relative pt-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <BannerSlider banners={banners} className="rounded-lg overflow-hidden" />
          </div>
        </section>
      )}

      {/* Hero Text Section */}
      <section className={`py-20 bg-white ${banners.length === 0 ? 'pt-36' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-gray-900 mb-6 animate-fade-in-up">
            Embark on Your Bridal Journey<br />
            with The Proposal Bridal
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            Discover your dream dress with our FREE Style Matcher and exclusive catalog.
            Your perfect dress is waiting for you.
          </p>
          <Link
            to="/signup"
            className="bg-black text-white hover:bg-gray-800 px-8 py-4 rounded-lg text-lg font-medium inline-block transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 animate-fade-in-up animation-delay-400"
          >
            Start Your Bridal Journey
          </Link>
        </div>
      </section>

      {/* Your Journey Begins Here */}
      <section className="py-20 bg-white bg-pattern-topography relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 text-center mb-16 animate-fade-in-up">
            Your Journey Begins Here
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Sign Up for Free */}
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 hover:scale-110 hover:shadow-lg">
                <ClipboardDocumentCheckIcon className="w-9 h-9 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Sign Up for Free</h3>
              <p className="text-gray-600">
                Create your account in seconds and start your bridal journey with us
              </p>
            </div>

            {/* Discover Your Style */}
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '400ms' }}>
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 hover:scale-110 hover:shadow-lg">
                {/* Dress silhouette inspired by The Proposal logo */}
                <svg className="w-9 h-9 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C11.5 2 11 2.5 11 3V5L8 4.5C8 4.5 6 8 6 12C6 16 3 20 3 22H21C21 22 18 16 18 12C18 8 16 4.5 16 4.5L13 5V3C13 2.5 12.5 2 12 2Z" />
                  <path d="M10 8C10 8 11 9 12 9C13 9 14 8 14 8" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Discover Your Style</h3>
              <p className="text-gray-600">
                Use our AI-powered Style Matcher to find dresses that match your unique taste
              </p>
            </div>

            {/* Browse Our Collection */}
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '600ms' }}>
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 hover:scale-110 hover:shadow-lg">
                <PhotoIcon className="w-9 h-9 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Browse Our Collection</h3>
              <p className="text-gray-600">
                Explore our exclusive catalog of handpicked designer dresses
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Your Trusted and Official Retailer */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 text-center mb-16">
            Your Trusted and Official Retailer
          </h2>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Curation from International Designers */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-xl group">
              <div className="aspect-[16/9] bg-gray-100 relative overflow-hidden">
                {designers.length > 0 ? (
                  <div className="w-full h-full grid grid-cols-3 grid-rows-2 gap-px bg-gray-200">
                    {designers.slice(0, 6).map((designer) => (
                      <div key={designer.id} className="bg-white flex items-center justify-center p-3">
                        {designer.logoUrl ? (
                          <img
                            src={designer.logoUrl}
                            alt={designer.name}
                            className="max-w-full max-h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                          />
                        ) : (
                          <span className="text-sm font-serif text-gray-700 text-center">{designer.name}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <SparklesIcon className="w-12 h-12 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Curation from International Designers</h3>
                <p className="text-gray-600 mb-4">
                  Discover our handpicked collection from renowned designers around the world.
                </p>
                {designers.length > 0 && (
                  <button
                    onClick={() => setShowDesignersModal(true)}
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                  >
                    View all designers →
                  </button>
                )}
              </div>
            </div>

            {/* Right: 2 rows */}
            <div className="flex flex-col gap-8">
              {/* Prepare with Ease */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-xl flex-1 flex flex-col sm:flex-row group">
                <div className="sm:w-2/5 aspect-[4/3] sm:aspect-auto bg-gray-100 relative overflow-hidden">
                  <img
                    src="https://www.theproposal.com.sg/wp-content/uploads/2022/11/d9.jpg"
                    alt="Prepare with Ease"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col justify-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Prepare with Ease</h3>
                  <p className="text-gray-600 text-sm">
                    As an authorized retailer, we work closely with our designers to understand their craftsmanship while sharing insights into our culture and the preferences of our brides.
                  </p>
                </div>
              </div>

              {/* Expertise */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-xl flex-1 flex flex-col sm:flex-row group">
                <div className="sm:w-2/5 aspect-[4/3] sm:aspect-auto bg-gray-100 relative overflow-hidden">
                  <img
                    src="https://www.theproposal.com.sg/wp-content/uploads/2022/11/d10.jpg"
                    alt="Expertise"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col justify-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Expertise</h3>
                  <p className="text-gray-600 text-sm">
                    Our team of bridal consultants and seamstresses are here to guide you every step of the way, creating a comfortable and seamless experience so you can focus on what truly matters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Designers Modal */}
      {showDesignersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDesignersModal(false)} />
          <div className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-serif text-gray-900">Our Designers</h3>
              <button
                onClick={() => setShowDesignersModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {designers.map((designer) => (
                <div
                  key={designer.id}
                  className="bg-gray-50 rounded-lg p-4 flex flex-col items-center text-center hover:bg-gray-100 transition-colors"
                >
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center mb-3 shadow-sm">
                    {designer.logoUrl ? (
                      <img
                        src={designer.logoUrl}
                        alt={designer.name}
                        className="max-w-full max-h-full object-contain p-1"
                      />
                    ) : (
                      <span className="text-xs font-serif text-gray-600">{designer.name}</span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-800">{designer.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Style Matcher Preview */}
      <section className="py-20 bg-white bg-pattern-waves">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 mb-6">
                Discover Your Style with Our Smart Matcher
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Simply swipe right on dresses you love, and our intelligent system learns your unique style preferences. 
                In just a few swipes, we'll curate a personalized collection just for you.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Swipe through beautiful dresses</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Get instant style recommendations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Save your favorites for later</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              {styleMatcherDresses.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {styleMatcherDresses.slice(0, 4).map((entry, index) => (
                    <div
                      key={entry.id}
                      className="aspect-[3/4] rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {entry.dress.thumbnailUrl ? (
                        <img
                          src={entry.dress.thumbnailUrl}
                          alt={entry.dress.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <SparklesIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-2xl p-8 shadow-lg">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg flex items-center justify-center relative overflow-hidden">
                    <div className="text-center">
                      <SparklesIcon className="w-16 h-16 text-primary-600 mx-auto mb-4 animate-float" />
                      <p className="text-gray-600">Style Matcher Preview</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Collection Preview — only render when loading or when there are dresses */}
      {(collectionLoading || collectionDresses.length > 0) && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 text-center mb-6">
              From Our Exclusive Collection
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Preview a selection of stunning dresses from our carefully curated catalog
            </p>
            {collectionLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-gray-200 rounded-lg"></div>
                    <div className="mt-3 h-4 bg-gray-200 rounded mx-auto w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {collectionDresses.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="group cursor-pointer animate-fade-in-up"
                    style={{ animationDelay: `${(index + 1) * 100}ms` }}
                  >
                    <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 relative">
                      {entry.dress.thumbnailUrl ? (
                        <img
                          src={entry.dress.thumbnailUrl}
                          alt={entry.dress.name}
                          className="w-full h-full object-cover object-center"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PhotoIcon className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <p className="mt-3 text-center text-gray-700 group-hover:text-primary-600 transition-colors font-medium">
                      {entry.dress.name}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-center mt-12 text-gray-600">
              And many more waiting to be discovered...
            </p>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        {/* Animated sparkles */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-sparkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            >
              <SparklesIcon className="w-4 h-4 text-primary-400/40" />
            </div>
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 mb-6 animate-fade-in-up">
            Begin your <span className="text-primary-600">#TheProposalJourney</span>
          </h2>
          <p className="text-lg text-gray-600 mb-10 animate-fade-in-up animation-delay-200">
            Be the first to know about our latest collections, exclusive trunk shows, and bridal events. Sign up for our newsletter and stay inspired.
          </p>

          {newsletterSubmitted ? (
            <div className="flex items-center justify-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-6 py-4 max-w-md mx-auto animate-fade-in-up">
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span>{newsletterMessage}</span>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto animate-fade-in-up animation-delay-200">
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={newsletterName}
                  onChange={(e) => setNewsletterName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all duration-300"
                />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all duration-300"
                />
                <label className="flex items-start gap-2 text-left text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletterConsent}
                    onChange={(e) => setNewsletterConsent(e.target.checked)}
                    required
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>I'd like to receive newsletters about new collections, trunk shows, and bridal events.</span>
                </label>
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="bg-black text-white hover:bg-gray-800 px-8 py-3 rounded-lg text-lg font-medium transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {newsletterLoading ? (
                    <span>Subscribing...</span>
                  ) : (
                    <>
                      <EnvelopeIcon className="w-5 h-5" />
                      <span>Subscribe</span>
                    </>
                  )}
                </button>
              </div>
              {newsletterError && (
                <p className="mt-3 text-sm text-red-600">{newsletterError}</p>
              )}
            </form>
          )}

          <p className="mt-8 text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300">
              Sign In
            </Link>
            {' '} | {' '}
            <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300">
              Create Your Free Account
            </Link>
          </p>
        </div>
      </section>

      {/* Where to Find Us */}
      {locations.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-900 text-center mb-16">
              Where to Find Us
            </h2>
            <div className={`grid gap-6 ${locations.length >= 2 ? 'md:grid-cols-2' : 'max-w-lg mx-auto'}`}>
              {locations.map((loc, index) => (
                <button
                  key={loc.id}
                  onClick={() => setActiveLocationId(loc.id)}
                  className="relative group rounded-xl overflow-hidden aspect-[16/9] cursor-pointer"
                >
                  <img
                    src={index === 0
                      ? 'https://www.theproposal.com.sg/wp-content/uploads/2022/11/d14.jpg'
                      : 'https://www.theproposal.com.sg/wp-content/uploads/2022/11/d15.jpg'}
                    alt={`${loc.name} showroom`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors duration-300" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <MapPinIcon className="w-8 h-8 text-white mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">{loc.name}</h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Location Modal */}
      {activeLocationId && (() => {
        const loc = locations.find(l => l.id === activeLocationId);
        if (!loc) return null;
        const addressEncoded = encodeURIComponent(loc.address || loc.name);
        const mapEmbedQuery = encodeURIComponent(loc.address || loc.name);
        const whatsappNumber = loc.whatsappNumber?.replace(/[^0-9]/g, '');
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setActiveLocationId(null)} />
            <div className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h3 className="text-xl font-serif text-gray-900">{loc.name}</h3>
                <button
                  onClick={() => setActiveLocationId(null)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Google Map */}
              <div className="aspect-video w-full">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapEmbedQuery}`}
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${loc.name} map`}
                />
              </div>

              <div className="p-6 space-y-5">
                {/* Address */}
                {loc.address && (
                  <div className="flex gap-3">
                    <MapPinIcon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Address</p>
                      <p className="text-gray-800 whitespace-pre-line">{loc.address}</p>
                    </div>
                  </div>
                )}

                {/* WhatsApp */}
                {whatsappNumber && (
                  <div className="flex gap-3">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">WhatsApp</p>
                      <a
                        href={`https://wa.me/${whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-800 hover:text-green-600 transition-colors inline-flex items-center gap-1.5"
                      >
                        {loc.whatsappNumber}
                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                )}

                {/* Get Directions */}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${addressEncoded}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Get Directions
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <footer className="text-gray-100 py-8 relative" style={{ backgroundColor: '#4e5251' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <img
            src={logo}
            alt="The Proposal Bridal"
            className="h-10 mx-auto mb-4"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <p className="text-sm">
            © {new Date().getFullYear()} The Proposal Bridal. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating Make Appointment Button */}
      <a
        href="https://linktr.ee/the.proposal.bridal"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 top-[15%] z-40 flex items-center gap-3 px-6 py-3.5 bg-primary-600 hover:bg-primary-700 text-white text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <CalendarDaysIcon className="h-6 w-6" />
        <span className="hidden sm:inline">Make Appointment</span>
        <span className="sm:hidden">Book</span>
      </a>
    </div>
  );
};

export default Landing;